import { useState, useRef, useEffect } from 'react'
import TextField from 'baby-ui/foundation/TextField'
import BabyUICheckbox from 'baby-ui/foundation/CheckBox'
import { Controller, useForm } from 'react-hook-form'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
  Typography,
} from '@material-ui/core'
import MaskedPhoneNumberTextField from 'baby-ui/foundation/MaskedPhoneNumberTextField'
import Alert from '@material-ui/lab/Alert'
import LoadingButton from 'baby-ui/compounds/LoadingButton'
import TermsAndConditions from 'baby-ui/compounds/TermsAndConditions'
import { observer } from 'mobx-react-lite'
import { useMutation } from 'react-query'
import { track, useTracking } from 'lib/babylistHealthAnalytics'
import DateTextField from 'baby-ui/compounds/DateTextField'
import useStyles from './styles'
import { ThemeConstants } from '../../styles'
import { CustomerInformationSchema } from './validationSchema'
import useCustomerInformationForm from './useCustomerInformationForm'
import { useDMEStore } from '../../store'
import customerInformationInputsToCustomerPayloadAdapter from './customerInformationInputsToCustomerPayloadAdapter'
import TermsAndConditionsWidget from '../../widgets/TermsAndConditionsWidget'
import { createOrUpdateCustomer } from '../../requests'
import { apiErrorResponseAdapter } from '../../utils'
import { formSubmittedEvent } from '../../events'
import { DME } from '../../../form-types'
import { useDebugger } from '../../utils/withDebugHelpers'
import { getDebugOperations } from './CustomerInformationForm.debug'
import { useLayout } from '../../widgets/Layout'
import EmailVerificationModal from './EmailVerificationModal/EmailVerificationModal'
import { getRemainingOverlayTime } from './CustomerInformationForm.utils'
import { PRIVACY_URL, TERMS_AND_CONDITIONS_PATH } from '../../constants'

const MEMBER_ID_HELP_TEXT =
  'Please include all digits and letters in your Member ID. Do not include dashes or spaces.'

export interface CustomerInformationFormProps {
  onSuccess(): void
}

type FormError = null | ReturnType<typeof apiErrorResponseAdapter>

const CustomerInformationForm = observer(
  ({ onSuccess }: CustomerInformationFormProps) => {
    const { handleSubmit, control, errors, setValue, getValues } = useForm({
      validationSchema: CustomerInformationSchema,
    })

    const [formErrors, setFormErrors] = useState<FormError>()

    const { isDebuggerEnabled, register } = useDebugger()

    const [submitStart, setSubmitStart] = useState<number | undefined>()
    const timerRef = useRef<ReturnType<typeof setInterval>>()

    useEffect(() => {
      if (isDebuggerEnabled) {
        const dependencies = {
          populateDependencies: { formSetter: setValue },
          setErrorDependencies: { formErrorSetter: setFormErrors },
        }
        register('CustomerInformationForm', getDebugOperations(dependencies))
      }

      // Clear the interval when the component unmounts
      return () => clearTimeout(timerRef.current)
    }, [])

    const store = useDMEStore()
    const classes = useStyles()

    const {
      agreedToAOB,
      agreedToTermsAndConditions,
      handleAOBAcknowledgmentChange,
      handleTermsAndConditionsChange,
      onAgreeToAOB,
      onAgreeToTermsAndConditions,
      onDisagreeToAOB,
      onDisagreeToTermsAndConditions,
      openDialog,
      showAOBDialog,
      showTermsAndConditionsDialog,
      submitAllowed,
      setOTPRequired,
      otpRequired,
      setEmailVerificationCompleted,
      completedEmailVerification,
    } = useCustomerInformationForm()

    const tracker = useTracking()

    const resetEmailVerificationFormState = () => {
      setOTPRequired(false)
    }

    const isOTPRequired = (
      response: APIResponse.Customer | APIResponse.CustomerOTPRequired
    ): response is APIResponse.CustomerOTPRequired =>
      (response as APIResponse.CustomerOTPRequired).otpRequired

    const waitMinimumOverlayTime = (callback: any) => {
      if (layout?.isInsuranceVerificationOverlayEnabled) {
        const waitTime = getRemainingOverlayTime(submitStart)
        timerRef.current = setTimeout(() => {
          store.ui.globalComponents.Overlay.close('insuranceVerification')
          callback()
        }, waitTime)
      } else {
        callback()
      }
    }

    const putCustomerSucceeded = (
      response: APIResponse.Customer | APIResponse.CustomerOTPRequired
    ) => {
      waitMinimumOverlayTime(() => onSuccessActions(response))
    }

    const onSuccessActions = (
      response: APIResponse.Customer | APIResponse.CustomerOTPRequired
    ) => {
      if (isOTPRequired(response)) {
        setOTPRequired(true)
        return
      }

      const { id, persistenceToken } = response as APIResponse.Customer

      formSubmittedEvent(tracker)({
        formName: track.FormName.CUSTOMER_INSURANCE_FORM,
        status: track.Status.SUCCESS,
      })
      store.user.update({ ...store.user, id, persistenceToken })

      if (completedEmailVerification) {
        store.ui.globalComponents.Snackbar.show(
          'Email verified. Any change of information has been updated on your account.',
          'success'
        )
      }

      onSuccess()
    }

    const putCustomerFailed = (e: any) => {
      waitMinimumOverlayTime(() => onFailureActions(e))
    }

    const onFailureActions = (e: any) => {
      const formattedError = apiErrorResponseAdapter(e)
      formSubmittedEvent(tracker)({
        formName: track.FormName.CUSTOMER_INSURANCE_FORM,
        status: track.Status.FAIL,
        errorMessage: formattedError.message
          ? formattedError.message
          : JSON.stringify(e),
      })

      setFormErrors(formattedError)
      if (isOTPRequired(e)) {
        setOTPRequired(true)
      }
    }

    const [putCustomer, { isLoading }] = useMutation(createOrUpdateCustomer, {
      onSuccess: putCustomerSucceeded,
      onError: putCustomerFailed,
    })

    const userIsLoadingOrSubmitted = isLoading || store.user.present
    const onSubmit = (data: DME.CustomerInformationFormInputs) => {
      if (layout?.isInsuranceVerificationOverlayEnabled) {
        store.ui.globalComponents.Overlay.show('insuranceVerification')
      }
      const payload = customerInformationInputsToCustomerPayloadAdapter({
        ...data,
        aobAcknowledged: agreedToAOB,
        gender: 'female',
        insuranceId: store.insurance.id!,
        insuranceState: store.insurance.state!,
      })
      setSubmitStart(new Date().getTime())

      putCustomer(payload)
    }

    const formErrorMessage = formErrors?.message

    const layout = useLayout()

    return (
      <>
        <TermsAndConditions
          clauses={[]}
          dialogContent={<TermsAndConditionsWidget />}
          open={showTermsAndConditionsDialog}
          title="Babylist Durable Medical Equipment Terms & Conditions"
          onAgree={onAgreeToTermsAndConditions}
          onDisagree={onDisagreeToTermsAndConditions}
        />

        <TermsAndConditions
          clauses={[
            {
              title: (
                <p>
                  Before we ship your product(s), we need you to authorize
                  Babylist Health LLC, a division of Babylist, to provide
                  products or services to you.
                </p>
              ),
              type: 'unordered',
              terms: [],
            },
            {
              title: (
                <p>
                  By agreeing below, you authorize{' '}
                  {store.insurance.billingProviderDisplayName} to submit claims
                  to your insurance company on your behalf and request on your
                  behalf all insurance information, prescriptions and benefits
                  for products provided as well as to release your medical
                  records to any person, organization, company and/or agency
                  which may be involved in providing your care.
                </p>
              ),
              type: 'unordered',
              terms: [],
            },
            {
              title: (
                <p>
                  <b>
                    By signing below, you also acknowledge that you have not
                    ordered another breast pump through your insurance for this
                    pregnancy.
                  </b>
                </p>
              ),
              type: 'unordered',
              terms: [],
            },
          ]}
          open={showAOBDialog}
          title="Assignment of Benefits"
          onAgree={onAgreeToAOB}
          onDisagree={onDisagreeToAOB}
        />

        <form onSubmit={handleSubmit(onSubmit as any)}>
          <EmailVerificationModal
            email={getValues().email}
            open={otpRequired}
            onClose={() => {
              resetEmailVerificationFormState()
            }}
            onFormSubmitted={() => {
              resetEmailVerificationFormState()
              setEmailVerificationCompleted()
              handleSubmit(onSubmit as any)()
            }}
          />

          <Grid container spacing={ThemeConstants.BASE_SPACING}>
            {formErrorMessage && (
              <Grid item xs={12}>
                <Alert severity="error" variant="standard">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: formErrorMessage,
                    }}
                  />
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Controller
                fullWidth
                required
                as={TextField}
                autoComplete="given-name"
                control={control}
                disabled={userIsLoadingOrSubmitted}
                error={errors.firstName || !!formErrors?.firstName}
                helperText={errors.firstName?.message || formErrors?.firstName}
                label="Patient's First Name"
                name="firstName"
                rules={{ required: true }}
                type="text"
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                fullWidth
                required
                as={TextField}
                autoComplete="family-name"
                control={control}
                disabled={userIsLoadingOrSubmitted}
                error={errors.lastName || !!formErrors?.lastName}
                helperText={errors.lastName?.message || formErrors?.lastName}
                label="Patient's Last Name"
                name="lastName"
                rules={{ required: true }}
                type="text"
              />
            </Grid>

            <Grid container item spacing={ThemeConstants.BASE_SPACING}>
              <Controller
                required
                as={DateTextField}
                control={control}
                day={{
                  placeholder: 'DD',
                  fullWidth: true,
                  autoComplete: 'bday-day',
                }}
                disabled={userIsLoadingOrSubmitted}
                error={errors.birthDate}
                helperText={errors.birthDate?.message}
                month={{
                  label: 'Date of Birth',
                  placeholder: 'MM',
                  fullWidth: true,
                  autoComplete: 'bday-month',
                }}
                name="birthDate"
                year={{
                  placeholder: 'YYYY',
                  fullWidth: true,
                  autoComplete: 'bday-year',
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                fullWidth
                required
                as={TextField}
                control={control}
                disabled={userIsLoadingOrSubmitted}
                error={!!formErrors?.memberId}
                helperText={formErrors?.memberId || MEMBER_ID_HELP_TEXT}
                label="Member ID"
                name="memberId"
                rules={{ required: true }}
              />
            </Grid>

            <Grid container item spacing={ThemeConstants.BASE_SPACING}>
              <Controller
                required
                as={DateTextField}
                control={control}
                day={{
                  placeholder: 'DD',
                  fullWidth: true,
                }}
                disabled={userIsLoadingOrSubmitted}
                error={errors.dueDate}
                helperText={errors.dueDate?.message}
                month={{
                  label: 'Due Date',
                  placeholder: 'MM',
                  fullWidth: true,
                }}
                name="dueDate"
                year={{
                  placeholder: 'YYYY',
                  fullWidth: true,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                fullWidth
                required
                as={TextField}
                control={control}
                disabled={userIsLoadingOrSubmitted}
                error={errors.email || !!formErrors?.email}
                helperText={errors.email?.message || formErrors?.email}
                label="Email"
                name="email"
                rules={{ required: true }}
                type="email"
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                fullWidth
                required
                as={MaskedPhoneNumberTextField}
                control={control}
                disabled={userIsLoadingOrSubmitted}
                error={errors.phoneNumber || !!formErrors?.phoneNumber}
                helperText={
                  errors.phoneNumber?.message || formErrors?.phoneNumber
                }
                label="Phone Number"
                name="phoneNumber"
                rules={{ required: true }}
                type="tel"
              />
            </Grid>

            <Grid item xs={12}>
              {layout?.isSmsOptInEnabled && (
                <Box mt={-3}>
                  <Controller
                    as={BabyUICheckbox}
                    control={control}
                    disabled={userIsLoadingOrSubmitted}
                    label={
                      <Box mt={3}>
                        I authorize Babylist Health to contact me by phone or
                        SMS
                        <br />
                        <Typography component="span" variant="caption">
                          (Babylist Health does not share or distribute this
                          information)
                        </Typography>
                      </Box>
                    }
                    name="smsAllowed"
                  />
                </Box>
              )}
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreedToTermsAndConditions}
                      disabled={userIsLoadingOrSubmitted}
                      inputProps={{
                        'aria-label': 'terms & conditions agreement',
                      }}
                      onChange={handleTermsAndConditionsChange}
                    />
                  }
                  label={
                    <span className={classes.termAndConditionsCopyWrapper}>
                      I agree to the&nbsp;
                      <Link
                        href={TERMS_AND_CONDITIONS_PATH}
                        target="_blank"
                        variant="body1"
                      >
                        Terms and Conditions
                      </Link>
                      &nbsp;and&nbsp;
                      <Link href={PRIVACY_URL} target="_blank" variant="body1">
                        Privacy Policy
                      </Link>
                    </span>
                  }
                  name="tAndC"
                />
              </div>
              <div>
                <FormControl error={!!formErrors?.aobAcknowledged}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agreedToAOB}
                        disabled={userIsLoadingOrSubmitted}
                        inputProps={{
                          'aria-label': 'assignment of benefits agreement',
                        }}
                        onChange={handleAOBAcknowledgmentChange}
                      />
                    }
                    label={
                      <span className={classes.termAndConditionsCopyWrapper}>
                        I agree to the&nbsp;
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <Link
                          component="button"
                          variant="body1"
                          onClick={(e) => {
                            // Preventing default behavior otherwise it will try to submit the form
                            e.preventDefault()
                            openDialog('aob')
                          }}
                        >
                          Assignment of Benefits
                        </Link>
                      </span>
                    }
                    name="aob"
                  />
                  {formErrors?.aobAcknowledged && (
                    <FormHelperText>
                      {formErrors.aobAcknowledged}
                    </FormHelperText>
                  )}
                </FormControl>
              </div>
            </Grid>
          </Grid>

          <LoadingButton
            fullWidth
            className={classes.verticalMargin}
            color="primary"
            disabled={!submitAllowed || userIsLoadingOrSubmitted}
            loading={isLoading}
            type="submit"
            variant="contained"
          >
            {store.insurance.requiresPrescription ? 'Continue' : 'Checkout'}
          </LoadingButton>
        </form>
      </>
    )
  }
)

export default CustomerInformationForm
